import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { localizedStoreProductName } from "../../../utils/storeUtils";
import ShinyText from "./ShinyText";
import styles from "./ProductCollections.module.css";
import Masonry from "./Masonry";

function hotspotProductId(entry) {
  const raw = entry?.productId;
  if (raw == null || raw === "") return "";
  if (typeof raw === "object" && raw._id != null) return String(raw._id);
  return String(raw);
}

/** First usable image URL for hotspot thumb (gallery, legacy `image`, color swatches). */
function pickHotspotProductImageUrl(product) {
  if (!product || typeof product !== "object") return "";
  const arr = Array.isArray(product.images) ? product.images : [];
  const heroIdx = Number(product?.heroImageIndex);
  if (Number.isInteger(heroIdx) && heroIdx >= 0 && heroIdx < arr.length) {
    const hi = arr[heroIdx];
    const hu = String(hi?.url || "").trim();
    if (hu) return hu;
    const hnb = String(hi?.noBgUrl || "").trim();
    if (hnb) return hnb;
  }
  for (let i = 0; i < arr.length; i += 1) {
    const img = arr[i];
    const u = String(img?.url || "").trim();
    if (u) return u;
    const nb = String(img?.noBgUrl || "").trim();
    if (nb) return nb;
  }
  const legacy = String(product?.image?.url || "").trim();
  if (legacy) return legacy;
  const colors = Array.isArray(product.colors) ? product.colors : [];
  for (let c = 0; c < colors.length; c += 1) {
    const cu = String(colors[c]?.imageUrl || "").trim();
    if (cu) return cu;
  }
  return "";
}

/** Prefer live catalog row (stock/pricing); fall back to embedded collections config product. */
function productForHotspot(entry, catalogById) {
  const pid = hotspotProductId(entry);
  if (pid && catalogById?.has(pid)) {
    return catalogById.get(pid);
  }
  const embedded = entry?.product;
  if (embedded && typeof embedded === "object") {
    const bid = embedded._id != null ? String(embedded._id) : "";
    if (bid && catalogById?.has(bid)) {
      return catalogById.get(bid);
    }
    return embedded;
  }
  return null;
}

function mergeProductImagesFromCatalog(embedded, catalogById) {
  if (!embedded || typeof embedded !== "object") return embedded;
  const id = embedded._id != null ? String(embedded._id) : "";
  if (!id || !catalogById?.has(id)) return embedded;
  if (pickHotspotProductImageUrl(embedded)) return embedded;
  const full = catalogById.get(id);
  if (!full || !pickHotspotProductImageUrl(full)) return embedded;
  return {
    ...embedded,
    images: Array.isArray(full.images) && full.images.length ? full.images : embedded.images,
    image: full.image?.url ? full.image : embedded.image,
  };
}

// Layout definitions (no default media). Actual media is driven by admin `collectionsConfig.tileMedia`.
const COLLECTIONS = [
  {
    id: "banner-top-right",
    image: "",
    video: "",
    link: "/store/category/all",
    height: 700,
    label: "Workspace collection",
    span: 2,
    alignRight: true,
  },
  { id: "1", image: "", link: "/store/category/all", height: 760, label: "Residential" },
  { id: "2", image: "", link: "/store/category/all", height: 650, label: "Workspace" },
  {
    id: "3",
    image: "",
    video: "",
    link: "/store/category/all",
    /* Masonry uses Math.floor(height / 2) for cell height — raise this to make the tile taller */
    height: 820,
    label: "Video",
  },
  { id: "4", image: "", link: "/store/category/all", height: 900, label: "Hospitality" },
  { id: "5", image: "", link: "/store/category/all", height: 475, label: "Seating" },
  { id: "6", image: "", link: "/store/category/all", height: 800, label: "Storage" },
  { id: "7", image: "", link: "/store/category/all", height: 650, label: "Tables" },
  { id: "8", image: "", label: "Decor", link: "/store/category/all", height: 550 },
  { id: "9", image: "", label: "Decor", link: "/store/category/all", height: 475 },
];

export default function ProductCollections({
  photosBasePath = "",
  collectionsConfig = null,
  resolveImageUrl = (value) => value,
  onAddToCart,
  /** Same list as `/store/products` — fills hotspot thumbs when embedded config omits image URLs. */
  catalogProducts = [],
}) {
  const { t, i18n } = useTranslation("global");
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");
  const navigate = useNavigate();

  const catalogById = React.useMemo(() => {
    const m = new Map();
    if (!Array.isArray(catalogProducts)) return m;
    catalogProducts.forEach((p) => {
      if (p && p._id != null) m.set(String(p._id), p);
    });
    return m;
  }, [catalogProducts]);

  const titleLead = t("store.collectionsTitleLead", { defaultValue: "Product" });
  const titleAccent = t("store.collectionsTitleAccent", { defaultValue: "collections" });

  const handleProductNavigate = React.useCallback(
    (productId) => {
      if (productId) navigate(`/store/product/${productId}`);
    },
    [navigate]
  );

  const hotspotProductIdsForItem = React.useCallback((item) => {
    const spots = Array.isArray(item?.hotspots)
      ? item.hotspots
      : item?.hotspot
      ? [item.hotspot]
      : [];
    const out = [];
    const seen = new Set();
    spots.forEach((h) => {
      const id =
        h?.product?._id != null
          ? String(h.product._id)
          : hotspotProductId(h);
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push(id);
    });
    return out;
  }, []);

  const handleCollectionTileClick = React.useCallback(
    (item) => {
      const ids = hotspotProductIdsForItem(item);
      if (ids.length > 0) {
        navigate(`/store/category/all?productIds=${encodeURIComponent(ids.join(","))}`);
        return;
      }
      navigate(item.url || "/store/category/all");
    },
    [navigate, hotspotProductIdsForItem]
  );
  const base = photosBasePath.replace(/\/?$/, "");

  const resolveCollectionMediaUrl = React.useCallback(
    (url) => {
      let value = String(url || "").trim();
      if (!value) return "";
      if (!/^https?:\/\//i.test(value) && !value.startsWith("/")) {
        if (
          value.startsWith("uploads/") ||
          value.startsWith("videos/") ||
          value.includes("/uploads/") ||
          value.includes("/videos/")
        ) {
          value = `/${value.replace(/^\/+/, "")}`;
        }
      }
      if (
        value.startsWith("http://") ||
        value.startsWith("https://") ||
        value.startsWith("/uploads/") ||
        value.startsWith("/videos/")
      ) {
        return resolveImageUrl(value);
      }
      if (value.startsWith("/photos/")) return value;
      return `${base}/${value}`;
    },
    [base, resolveImageUrl]
  );

  const dynamicCollections = React.useMemo(() => {
    const tileMediaMap = new Map(
      (Array.isArray(collectionsConfig?.tileMedia) ? collectionsConfig.tileMedia : [])
        .filter((item) => item?.tileId)
        .map((item) => [String(item.tileId), item])
    );

    const buildHotspot = (entryHotspot, fallbackHotspot = null) => {
      let product = productForHotspot(entryHotspot, catalogById);
      if (!product) return fallbackHotspot;
      product = mergeProductImagesFromCatalog(product, catalogById);
      const firstProductImage = pickHotspotProductImageUrl(product);
      const resolvedThumb = firstProductImage
        ? resolveCollectionMediaUrl(firstProductImage)
        : "";
      return {
        title:
          localizedStoreProductName(product, i18n.language) ||
          fallbackHotspot?.title ||
          "Product",
        details: `${product.category || "Store"} collection`,
        image: resolvedThumb,
        x: Number.isFinite(Number(entryHotspot?.x))
          ? Number(entryHotspot.x)
          : fallbackHotspot?.x || 50,
        y: Number.isFinite(Number(entryHotspot?.y))
          ? Number(entryHotspot.y)
          : fallbackHotspot?.y || 50,
        product,
      };
    };

    return COLLECTIONS.map((item) => {
      const tileConfig = tileMediaMap.get(String(item.id)) || null;
      const mediaType = String(tileConfig?.mediaType || "").toLowerCase();
      const mediaUrl = resolveCollectionMediaUrl(tileConfig?.mediaUrl || tileConfig?.imageUrl || "");

      let nextImage = item.image;
      let nextVideo = item.video || "";
      if (mediaType === "image" && mediaUrl) {
        nextImage = mediaUrl;
        nextVideo = "";
      } else if (mediaType === "video" && mediaUrl) {
        nextVideo = mediaUrl;
        nextImage = "";
      } else if (mediaType === "default") {
        nextImage = item.image;
        nextVideo = item.video || "";
      }

      // Backward compatibility for older top-tile config.
      if (item.id === "banner-top-right" && !tileConfig) {
        const legacyPhotoUrl = resolveCollectionMediaUrl(collectionsConfig?.photoUrl || "");
        if (legacyPhotoUrl) {
          nextImage = legacyPhotoUrl;
          nextVideo = "";
        }
      }

      const rawHotspotEntries = [];
      if (Array.isArray(tileConfig?.hotspots) && tileConfig.hotspots.length > 0) {
        rawHotspotEntries.push(...tileConfig.hotspots);
      } else if (
        tileConfig?.hotspot &&
        (tileConfig.hotspot.product || tileConfig.hotspot.productId)
      ) {
        rawHotspotEntries.push(tileConfig.hotspot);
      } else if (item.id === "banner-top-right") {
        const legacy = collectionsConfig?.hotspot;
        if (legacy && (legacy.product || legacy.productId)) {
          rawHotspotEntries.push(legacy);
        }
      }

      let hotspotsResolved = rawHotspotEntries
        .map((h) => buildHotspot(h, item.hotspot || null))
        .filter(Boolean);

      if (!hotspotsResolved.length && item.hotspot) {
        const fh = item.hotspot;
        hotspotsResolved = [
          {
            title: fh.title || "",
            details: fh.details || "",
            image: fh.image ? resolveCollectionMediaUrl(fh.image) : "",
            x: Number.isFinite(Number(fh.x)) ? Number(fh.x) : 50,
            y: Number.isFinite(Number(fh.y)) ? Number(fh.y) : 50,
          },
        ];
      }

      return {
        ...item,
        image: nextImage,
        video: nextVideo,
        hotspots: hotspotsResolved,
        hotspot: hotspotsResolved[0] || null,
      };
    });
  }, [catalogById, collectionsConfig, resolveCollectionMediaUrl, i18n.language]);

  const items = React.useMemo(
    () =>
      dynamicCollections
        .map((item) => ({
          id: item.id,
          img: resolveCollectionMediaUrl(item.image),
          video: item.video || "",
          url: item.link || "/store/category/all",
          height: item.height || 420,
          label: item.label || "collection",
          span: item.span || 1,
          alignRight: Boolean(item.alignRight),
          hotspots: (item.hotspots || []).map((h) => ({
            ...h,
            image: String(h.image || "").trim(),
          })),
          hotspot: item.hotspots?.[0]
            ? {
                ...item.hotspots[0],
                image: String(item.hotspots[0].image || "").trim(),
              }
            : null,
        }))
        // Hide tiles that have no admin media yet (prevents placeholders).
        .filter((item) => Boolean(item.img) || Boolean(item.video)),
    [dynamicCollections, resolveCollectionMediaUrl]
  );
  const hasCollections = items.length > 0;

  const titleAccentNode = (
    <span
      className={styles.titleAccent}
      dir={isRtl ? "rtl" : "ltr"}
      lang={isRtl ? "ar" : "en"}
    >
      <ShinyText
        text={titleAccent}
        speed={2}
        delay={0}
        color="#6f6458"
        shineColor="#ffffff"
        spread={120}
        direction={isRtl ? "right" : "left"}
        yoyo={false}
        pauseOnHover={false}
        disabled={false}
      />
    </span>
  );

  const titleLeadNode = (
    <span className={styles.titleLead} dir={isRtl ? "rtl" : "ltr"} lang={isRtl ? "ar" : "en"}>
      {titleLead}
    </span>
  );

  return (
    <section className={styles.wrap} aria-labelledby="product-collections-heading">
      <div
        className={styles.section}
        dir={isRtl ? "rtl" : "ltr"}
        lang={isRtl ? "ar" : "en"}
      >
        <h2 id="product-collections-heading" className={styles.title}>
          {isRtl ? (
            <>
              {titleAccentNode}
              {titleLeadNode}
            </>
          ) : (
            <>
              {titleLeadNode}
              {titleAccentNode}
            </>
          )}
        </h2>
        <p className={styles.subtitle}>
          {t("store.collectionsSubtitle", {
            defaultValue: "Explore product collections from our vendors",
          })}
        </p>
        {!hasCollections ? (
          <div className={styles.masonryWrap}>
            <p className={styles.subtitle} style={{ margin: 0 }}>
              {t("store.collectionsEmpty", {
                defaultValue:
                  "Collections will appear here once they are added by the admin.",
              })}
            </p>
          </div>
        ) : (
          <div className={styles.masonryWrap}>
            <Masonry
              items={items}
              ease="power3.out"
              duration={0.6}
              stagger={0.05}
              animateFrom="bottom"
              scaleOnHover
              hoverScale={0.95}
              blurToFocus
              colorShiftOnHover={false}
              onItemClick={handleCollectionTileClick}
              onAddToCart={onAddToCart}
              onProductNavigate={handleProductNavigate}
            />
          </div>
        )}
      </div>
    </section>
  );
}
